import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { getAllDatesFromSelection } from "../../../../Utils/StayUpToDate/RenderComponent";
import { computeWindow } from "../../../../Utils/customHooks/UpToDateContent/useLoadYesterdayLinks";

dayjs.extend(utc);
dayjs.extend(timezone);

export type ProductFeedTimeframe =
  | "yesterday"
  | "this-week"
  | "this-month"
  | "by-the-day";
export type ProductFeedOrder = "DESC" | "ASC" | "CUSTOM";

/** Recent Products / product carousels always use product platforms only. */
export const PRODUCTS_ONLY_SHOW_FILTER = "products" as const;

export type ProductFeedSelection = {
  timeframe: ProductFeedTimeframe;
  title: string;
  displayName: string;
  type: string;
  days: number[];
  details: "" | number[];
};

export const PRODUCT_FEED_TIMEFRAMES: Array<{
  id: ProductFeedTimeframe;
  label: string;
}> = [
  { id: "yesterday", label: "Yesterday" },
  { id: "this-week", label: "This Week" },
  { id: "this-month", label: "This Month" },
  { id: "by-the-day", label: "By the Day" },
];

export const PRODUCT_FEED_DAY_OPTIONS = [
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
  "Sunday",
  "This Weekend",
] as const;

export const PRODUCT_FEED_DEFAULTS: Record<
  ProductFeedTimeframe,
  ProductFeedSelection
> = {
  yesterday: {
    timeframe: "yesterday",
    title: "Yesterday's Shoppable Content",
    displayName: "Yesterday",
    type: "Yesterday",
    days: [1],
    details: "",
  },
  "this-week": {
    timeframe: "this-week",
    title: "This Week's Shoppable Content",
    displayName: "This Week",
    type: "This Week",
    days: [7],
    details: [7],
  },
  "this-month": {
    timeframe: "this-month",
    title: "This Month's Shoppable Content",
    displayName: "This Month",
    type: "This Month",
    days: [30],
    details: [30],
  },
  "by-the-day": {
    timeframe: "by-the-day",
    title: "Shoppable Content by the Day",
    displayName: "",
    type: "daywise",
    days: [],
    details: "",
  },
};

export function stayUpComponentsList(stayUp: any): any[] {
  if (!stayUp) return [];
  if (Array.isArray(stayUp)) return stayUp;
  const rows = [
    ...(stayUp?.data || []),
    ...(stayUp?.showingComponents || []),
    ...(stayUp?.archiveComponents || []),
  ];
  const seen = new Set<string>();
  return rows.filter((obj: any) => {
    const id = obj?.id != null ? String(obj.id) : "";
    if (!id) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function hasActiveProductFeedTimeframe(
  stayUp: any,
  displayName: string
): boolean {
  const want = displayName.toLowerCase().trim();
  return stayUpComponentsList(stayUp).some((obj: any) => {
    const status = String(obj?.status_expiry ?? obj?.status ?? "")
      .toLowerCase()
      .trim();
    if (status === "hidden" || status === "deleted") return false;
    const dn = String(obj?.display_name ?? "").toLowerCase().trim();
    const type = String(obj?.type ?? "").toLowerCase().trim();
    return dn === want || type === want;
  });
}

export function getDisabledProductFeedTimeframes(
  _stayUp: any
): Set<ProductFeedTimeframe> {
  return new Set();
}

export function isDayWiseTimeframe(timeframe: ProductFeedTimeframe): boolean {
  return timeframe === "by-the-day";
}

export function getDayWiseAutoTitle(days: string[]): string {
  const names = (days || []).map((day) => String(day).trim()).filter(Boolean);
  if (!names.length) return PRODUCT_FEED_DEFAULTS["by-the-day"].title;
  return `${names.join(" & ")}'s Shoppable Content`;
}

/** Previously prefilled product-feed titles, now used as the preview type heading. */
export function getProductFeedTypeLabel(
  timeframe: ProductFeedTimeframe,
  selectedDays: string[] = []
): string {
  if (timeframe === "by-the-day") return getDayWiseAutoTitle(selectedDays);
  return PRODUCT_FEED_DEFAULTS[timeframe].title;
}

export function resolveProductFeedSelection(
  timeframe: ProductFeedTimeframe
): ProductFeedSelection {
  return PRODUCT_FEED_DEFAULTS[timeframe];
}

export function getDefaultProductFeedTimeframe(
  disabled: ReadonlySet<ProductFeedTimeframe>
): ProductFeedTimeframe {
  return (
    PRODUCT_FEED_TIMEFRAMES.find((option) => !disabled.has(option.id))?.id ||
    "yesterday"
  );
}

/** Product Feed always defaults to newest-first, including This Week and By the Day. */
export function getDefaultProductFeedOrder(
  _timeframe: ProductFeedTimeframe
): ProductFeedOrder {
  return "DESC";
}

export function isProductFeedDefaultTitle(title: string): boolean {
  const value = String(title || "").trim();
  return PRODUCT_FEED_TIMEFRAMES.some(
    (option) => PRODUCT_FEED_DEFAULTS[option.id].title === value
  );
}

export function isProductFeedManagedTitle(
  title: string,
  selectedDays: string[] = []
): boolean {
  const value = String(title || "").trim();
  if (isProductFeedDefaultTitle(value)) return true;
  if (!selectedDays.length) return false;
  return value === getDayWiseAutoTitle(selectedDays);
}

export function isStayUpComponentVisible(obj: any): boolean {
  const status = String(obj?.status_expiry ?? obj?.status ?? "")
    .toLowerCase()
    .trim();
  return status !== "hidden" && status !== "deleted";
}

export function isStayUpComponentPublished(obj: any): boolean {
  if (!obj || !isStayUpComponentVisible(obj)) return false;
  const status = String(obj?.status_expiry ?? obj?.status ?? "")
    .toLowerCase()
    .trim();
  return status === "active" || status === "";
}

const DAY_TOKENS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
  "this weekend",
];

export function parseProductFeedTimeframeFromComponent(
  component: any
): ProductFeedTimeframe | null {
  if (!component) return null;
  const dn = String(component?.display_name ?? "").toLowerCase().trim();
  const type = String(component?.type ?? "").toLowerCase().trim();
  if (dn === "yesterday" || type === "yesterday") return "yesterday";
  if (dn === "this week" || type === "this week") return "this-week";
  if (dn === "this month" || type === "this month") return "this-month";
  if (
    type === "daywise" ||
    DAY_TOKENS.some((token) => dn.split(",").map((part) => part.trim()).includes(token))
  ) {
    return "by-the-day";
  }
  return null;
}

export function findProductFeedComponent(
  stayUp: any,
  timeframe: ProductFeedTimeframe,
  options?: { preferredId?: string | number | null }
): any | null {
  const preferredId =
    options?.preferredId != null ? String(options.preferredId) : null;
  const matches = stayUpComponentsList(stayUp).filter((obj: any) => {
    if (!isStayUpComponentVisible(obj)) return false;
    return parseProductFeedTimeframeFromComponent(obj) === timeframe;
  });
  if (!matches.length) return null;
  if (preferredId) {
    const preferred = matches.find((obj: any) => String(obj?.id) === preferredId);
    if (preferred) return preferred;
  }
  return (
    matches.find((obj: any) => isStayUpComponentPublished(obj)) || matches[0]
  );
}

/** Edit always stays on the opened row; timeframe changes convert that component. */
export function resolveProductFeedEditTarget(
  _stayUp: any,
  _timeframe: ProductFeedTimeframe,
  opened: any
): any | null {
  return opened || null;
}

export function parseDayWiseSelectedDays(component: any): string[] {
  if (parseProductFeedTimeframeFromComponent(component) !== "by-the-day") {
    return [];
  }
  return String(component?.display_name || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
}

export type ProductFeedStorageFields = {
  type: string;
  displayName: string;
  details: "" | number[];
  componentType?: "daywise";
};

/** SQL window for product daywise feeds — mirrors useLoadYesterdayLinks / LiB. */
export function getProductDayWiseShoplinkWindow(
  selectedDays: string[],
  tz: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
): {
  startDate: string;
  endDate: string;
  selectedDates: string[];
} | null {
  if (!selectedDays.length) return null;
  const windowRange = computeWindow([], "daywise", selectedDays);
  if (!windowRange.start || !windowRange.end) return null;
  const selectedDates = getAllDatesFromSelection(selectedDays).map((dateStr) =>
    dayjs(new Date(dateStr)).tz(tz).format("YYYY-MM-DD"),
  );
  if (!selectedDates.length) return null;
  return {
    startDate: windowRange.start.split(" ")[0],
    endDate: windowRange.end.split(" ")[0],
    selectedDates,
  };
}

export const PRODUCT_FEED_TIMEFRAME_LABEL: Record<ProductFeedTimeframe, string> =
  {
    yesterday: "Yesterday",
    "this-week": "This Week",
    "this-month": "This Month",
    "by-the-day": "By the Day",
  };

export const PRODUCT_FEED_TIMEFRAME_FROM_LABEL: Record<
  string,
  ProductFeedTimeframe
> = {
  Yesterday: "yesterday",
  "This Week": "this-week",
  "This Month": "this-month",
  "By the Day": "by-the-day",
};

export function getProductFeedStorageFields(
  timeframe: ProductFeedTimeframe,
  selectedDays: string[] = []
): ProductFeedStorageFields {
  if (timeframe === "by-the-day") {
    return {
      type: "daywise",
      displayName: selectedDays.join(","),
      details: "",
      componentType: "daywise",
    };
  }
  const selection = PRODUCT_FEED_DEFAULTS[timeframe];
  return {
    type: selection.type,
    displayName: selection.displayName || selection.type,
    details: selection.details,
  };
}

export function shouldClearPublishedProductFeedTitle(args: {
  component: any;
  openedTimeframe: ProductFeedTimeframe | null;
  nextTimeframe: ProductFeedTimeframe;
}): boolean {
  if (!isStayUpComponentPublished(args.component)) return false;
  if (!args.openedTimeframe) return false;
  return args.openedTimeframe !== args.nextTimeframe;
}

export function buildProductFeedEditorComponent(
  opened: any,
  timeframe: ProductFeedTimeframe,
  selectedDays: string[] = []
): any {
  if (!opened) return opened;
  const openedTimeframe = parseProductFeedTimeframeFromComponent(opened);
  const storage = getProductFeedStorageFields(timeframe, selectedDays);
  const sameTimeframe = openedTimeframe === timeframe;
  const sameDays =
    timeframe !== "by-the-day" ||
    parseDayWiseSelectedDays(opened).join(",") === selectedDays.join(",");
  if (sameTimeframe && sameDays) return opened;

  const clearTitle = shouldClearPublishedProductFeedTitle({
    component: opened,
    openedTimeframe,
    nextTimeframe: timeframe,
  });
  return {
    ...opened,
    type: storage.type,
    display_name: storage.displayName,
    details: storage.details,
    name: clearTitle ? "" : opened.name,
    post_ids: sameTimeframe ? opened.post_ids : "",
    removed_postids: sameTimeframe ? opened.removed_postids : "",
    custom_order: sameTimeframe ? opened.custom_order : "",
    query: opened.query,
  };
}
